import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FaChevronDown, FaSearch } from 'react-icons/fa/index.js';
import '../../styles/CustomDropdown.css';

const CustomDropdown = ({
    options,
    value,
    onChange,
    placeholder = 'Select...',
    label,
    searchable = true,
    isLoading = false,
    selectedValues = null, // Optional Set of values to highlight
    keepOpenOnSelect = false // New prop
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const filteredOptions = options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Performance optimization: Only render first 100 options
    // The user can filter to find specific ones. Rendering 5000 nodes kills the DOM.
    const displayOptions = filteredOptions.slice(0, 100);

    const handleSelect = (option) => {
        onChange(option.value);
        if (!keepOpenOnSelect) {
            setIsOpen(false);
            setSearchTerm('');
        }
    };

    const selectedOption = options.find((opt) => opt.value === value);

    const isOptionSelected = (optValue) => {
        if (value === optValue) return true;
        if (selectedValues && selectedValues.has(optValue)) return true;
        return false;
    };

    return (
        <div className="custom-dropdown-container" ref={dropdownRef}>
            {label && <label className="custom-dropdown-label">{label}</label>}
            <div
                className={`custom-dropdown-trigger ${isOpen ? 'open' : ''}`}
                onClick={() => setIsOpen(!isOpen)}>
                <span>
                    {selectedOption ? selectedOption.label : placeholder}
                </span>
                <FaChevronDown className="dropdown-arrow" />
            </div>

            {isOpen && (
                <div className="custom-dropdown-menu">
                    {searchable && (
                        <div className="dropdown-search">
                            <FaSearch className="search-icon" />
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                autoFocus
                            />
                        </div>
                    )}
                    <div className="dropdown-options">
                        {(() => {
                            if (isLoading) {
                                return (
                                    <div className="dropdown-loading">
                                        Loading options...
                                    </div>
                                );
                            }

                            if (displayOptions.length === 0) {
                                return (
                                    <div className="dropdown-no-results">
                                        No results found
                                    </div>
                                );
                            }

                            return (
                                <>
                                    {displayOptions.map((option) => (
                                        <div
                                            key={option.value}
                                            className={`dropdown-option ${
                                                isOptionSelected(option.value)
                                                    ? 'selected'
                                                    : ''
                                            }`}
                                            onClick={() =>
                                                handleSelect(option)
                                            }>
                                            {option.label}
                                        </div>
                                    ))}
                                    {filteredOptions.length > 100 && (
                                        <div className="dropdown-more-results">
                                            ... {filteredOptions.length - 100}{' '}
                                            more results (type to filter)
                                        </div>
                                    )}
                                </>
                            );
                        })()}
                    </div>
                </div>
            )}
        </div>
    );
};

CustomDropdown.propTypes = {
    options: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
                .isRequired,
            label: PropTypes.string.isRequired
        })
    ).isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    label: PropTypes.string,
    searchable: PropTypes.bool,
    isLoading: PropTypes.bool,
    selectedValues: PropTypes.instanceOf(Set), // New prop type
    keepOpenOnSelect: PropTypes.bool
};

export default CustomDropdown;
